import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModakBackdrop from "./ModakBackdrop";
import { useRoundGate } from "./useRoundGate";
import { useElapsedTimer } from "./useElapsedTimer";
import { submitModakRound } from "./modakApi";
import { RoundHeader } from "./RoundChrome";

const CORRECT_WORDS = [
  "Kapila", "Vinayaka", "Vikata", "Harasuna", "Vatave", "Hematunda", "Ekadanta",
  "Vakratunda", "Guhagraja", "Sarveshwara", "Heramba", "Suragraja", "Chaturhotra",
  "Lambodara", "Gajamukha", "Umaputra", "Shurpakarna", "Ganadhisha", "Siddhi-Vinayaka",
  "Bhalachandra", "Sumukha",
];
const WRONG_WORDS = ["Vishnu", "Shree-Ram", "Murugunna", "Krishna", "Mahadev"];
const ALL_WORDS = [...CORRECT_WORDS, ...WRONG_WORDS];

const RULES = ["Identify the names of Ganesha", "Right answer will turn the words green wrong answer will turn the words black"];

// Word-cloud generators place words at various rotations, so a rotated
// word's axis-aligned bounding box often has empty corners the diagonal
// glyph never actually reaches — an exact click there hits the full-canvas
// background rect (or nothing) instead of the word, even when the user
// visually aimed correctly. Falls back to the nearest not-yet-answered
// word within NEAREST_WORD_PX of the click when the exact hit-test misses.
const NEAREST_WORD_PX = 28;

function findNearestWord(container, clickX, clickY) {
  let best = null;
  let bestDist = Infinity;
  for (const w of ALL_WORDS) {
    for (const el of container.getElementsByClassName(w)) {
      if (el.classList.contains("modak-word-disabled")) continue;
      const rect = el.getBoundingClientRect();
      const dx = Math.max(rect.left - clickX, 0, clickX - rect.right);
      const dy = Math.max(rect.top - clickY, 0, clickY - rect.bottom);
      const dist = Math.hypot(dx, dy);
      if (dist < bestDist) {
        bestDist = dist;
        best = { el, word: w };
      }
    }
  }
  return bestDist <= NEAREST_WORD_PX ? best : null;
}

export default function Round3() {
  const navigate = useNavigate();
  const { loading, user, adminToken, timerKey, status } = useRoundGate(3);
  const time = useElapsedTimer(timerKey);

  const [svgHtml, setSvgHtml] = useState("");
  const [score, setScore] = useState(0);
  const [counter, setCounter] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const scoreRef = useRef(0);
  const counterRef = useRef(0);
  const submittedRef = useRef(false);
  const basePointsRef = useRef(0);
  // Unlike every other round, this one used to submit answers: "" — leaving
  // no forensic trail to tell a genuine misclick on a decoy word apart from
  // an actual scoring bug when points come up short of 21.
  const clickedWordsRef = useRef([]);

  useEffect(() => {
    fetch("/modak-assets/home/wordcloud23.svg")
      .then((r) => r.text())
      .then(setSvgHtml);
  }, []);

  useEffect(() => {
    if (status) {
      scoreRef.current = status.points ?? 0;
      basePointsRef.current = scoreRef.current;
      setScore(scoreRef.current);
    }
  }, [status]);

  const finish = useCallback(
    async (finalPoints) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);
      await submitModakRound(adminToken, {
        userId: user.userId,
        round: 3,
        points: finalPoints,
        time,
        answers: clickedWordsRef.current.join(","),
        user,
      });
      navigate("/home");
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adminToken, user, time, navigate]
  );

  if (loading) return null;

  const handleCloudClick = (e) => {
    // Guards against fast repeat taps registering more clicks while `finish()`
    // is still in flight (it's async, so the counter could otherwise sail
    // past 21 before navigation away from the round actually happens).
    if (counterRef.current >= 21 || submittedRef.current) return;
    let node = e.target;
    let match = null;
    while (node && node.tagName !== "svg" && node.tagName !== undefined) {
      if (node.classList) {
        let found;
        for (const w of ALL_WORDS) {
          if (node.classList.contains(w)) {
            found = w;
            break;
          }
        }
        if (found) {
          match = { el: node, word: found };
          break;
        }
      }
      node = node.parentElement;
    }
    if (!match) {
      match = findNearestWord(e.currentTarget, e.clientX, e.clientY);
    }
    if (!match) return;
    const { el, word } = match;
    if (el.classList.contains("modak-word-disabled")) return;

    const isCorrectWord = CORRECT_WORDS.includes(word);
    clickedWordsRef.current.push(`${word}:${isCorrectWord ? "correct" : "wrong"}`);
    // The word cloud draws each word as 2+ separate elements (duplicates for
    // visual density) sharing the same class — disable every occurrence of
    // this word, not just the one clicked, so a later tap on its twin can't
    // silently burn one of the 21 clicks without earning a new point.
    const container = e.currentTarget;
    const occurrences = container.getElementsByClassName(word);
    Array.from(occurrences).forEach((occ) => {
      occ.style.fill = isCorrectWord ? "green" : "black";
      occ.classList.add("modak-word-disabled");
    });

    // Counter tracks correct words found, not raw clicks — the rules only
    // ever promised a wrong click turns black, never that it costs part of
    // your 21. A wrong click still marks/disables the word so it can't be
    // clicked again, but doesn't touch the counter or score.
    if (!isCorrectWord) return;

    const nextCounter = counterRef.current + 1;
    counterRef.current = nextCounter;
    setCounter(nextCounter);

    const nextScore = scoreRef.current + 1;
    scoreRef.current = nextScore;
    setScore(nextScore);

    if (nextCounter === 21) {
      finish(nextScore);
    }
  };

  const handleSubmit = () => {
    if (submitting) return;
    finish(scoreRef.current);
  };

  const handleSkip = () => {
    if (submitting) return;
    finish(basePointsRef.current);
  };

  return (
    <ModakBackdrop>
      <div className="modak-round">
        <RoundHeader time={time} score={score} rules={RULES} onSkip={handleSkip} />

        <div className="modak-round-heading-row">
          <span className="modak-round-label">Name-a-thon</span>
          <div className="modak-round-instruction">Identify the correct names of Lord Ganesh from this cloud</div>
          <div className="modak-r3-counter">Counter: {counter}/21</div>
        </div>

        <div
          className="modak-r3-cloud-wrap"
          onClick={handleCloudClick}
          dangerouslySetInnerHTML={{ __html: svgHtml }}
        />

        {counter >= 5 && (
          <button type="button" className="modak-btn modak-submit-btn" disabled={submitting} onClick={handleSubmit}>
            Submit
          </button>
        )}
      </div>
    </ModakBackdrop>
  );
}
